import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ProFeatureGate } from "@/components/ProFeatureGate";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, Linkedin, Sparkles, Gauge, Copy, Download, Info, TrendingUp } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const SENIORITY = ["Entry-level", "Mid-level", "Senior", "Lead/Principal", "Manager", "Director", "Executive"];
const SUBS = [
  ["completeness", "Completeness"], ["keyword_alignment", "Keyword Alignment"],
  ["headline_summary", "Headline & Summary"], ["achievement_orientation", "Achievement Focus"], ["structure", "Structure"],
];

const copy = (t) => { navigator.clipboard.writeText(t || ""); toast.success("Copied"); };

function ProgressCard({ history }) {
  const data = history.map((h) => ({
    label: new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    score: h.overall_score,
    role: h.role,
  }));
  const first = history[0].overall_score;
  const last = history[history.length - 1].overall_score;
  const delta = last - first;
  return (
    <Card className="p-6 border-zinc-200" data-testid="linkedin-progress-card">
      <h2 className="font-heading text-lg font-bold mb-1 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-accent" /> Your progress</h2>
      <p className="text-sm text-muted-foreground mb-4">Apply the optimised content on LinkedIn, export a fresh PDF, then re-upload and re-analyse to watch your score climb.</p>
      {history.length >= 2 ? (
        <>
          <p className="text-sm mb-3" data-testid="li-progress-delta">
            <span className={`font-semibold ${delta >= 0 ? "text-green-700" : "text-red-600"}`}>{delta >= 0 ? `+${delta}` : delta} points</span>
            <span className="text-muted-foreground"> since your first analysis ({first} → {last})</span>
          </p>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={data} margin={{ top: 5, right: 12, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="#166534" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <p className="text-sm" data-testid="li-progress-single">First analysis: <strong>{first}/100</strong> for {history[0].role}. Analyse again after optimising to track your improvement over time.</p>
      )}
    </Card>
  );
}

function ScoreBar({ label, value }) {
  const color = value >= 80 ? "bg-green-500" : value >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">{label}</span><span className="font-semibold">{value}</span></div>
      <div className="h-2 rounded-full bg-zinc-100"><div className={`h-2 rounded-full ${color}`} style={{ width: `${value}%` }} /></div>
    </div>
  );
}

export default function LinkedInOptimize() {
  const [isPro, setIsPro] = useState(null);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [sections, setSections] = useState(null);
  const [role, setRole] = useState("");
  const [seniority, setSeniority] = useState("Mid-level");
  const [settingTarget, setSettingTarget] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizations, setOptimizations] = useState([]);
  const [history, setHistory] = useState([]);

  const loadCurrent = async () => {
    try {
      const { data } = await api.get("/linkedin/current");
      setCurrent(data);
      if (data.has_profile) {
        setSections(data.sections);
        setRole(data.target_role || "");
        setSeniority(data.seniority || "Mid-level");
      }
      const opt = await api.get("/linkedin/optimizations").catch(() => ({ data: [] }));
      setOptimizations(opt.data);
      const hist = await api.get("/linkedin/history").catch(() => ({ data: [] }));
      setHistory(Array.isArray(hist.data) ? hist.data : []);
    } catch (e) { /* pro gate handles */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/subscription");
        const pro = data.status === "active" && ["pro", "sponsored"].includes(data.tier);
        setIsPro(pro);
        if (pro) await loadCurrent();
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/linkedin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSections(data.sections);
      toast.success("Profile extracted — review and edit below.");
      await loadCurrent();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const saveReview = async () => {
    setSavingReview(true);
    try {
      await api.put("/linkedin/review", { sections });
      toast.success("Saved. Now pick your target role.");
      await loadCurrent();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingReview(false); }
  };

  const setTarget = async () => {
    if (!role.trim()) { toast.error("Enter a target role"); return; }
    setSettingTarget(true);
    try {
      const { data } = await api.post("/linkedin/target", { role, seniority });
      toast.success(`Target set. Adjacent roles: ${data.adjacent_roles.join(", ") || "—"}`);
      await loadCurrent();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSettingTarget(false); }
  };

  const runAnalyze = async () => {
    setAnalyzing(true);
    try {
      await api.post("/linkedin/analyze");
      // poll until analysis completes (LLM runs in background)
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data } = await api.get("/linkedin/current");
        if (data.analysis_status === "done") { setCurrent(data); toast.success("Analysis complete"); break; }
        if (data.analysis_status === "error") { toast.error("Analysis failed. Please try again."); await loadCurrent(); break; }
      }
      await loadCurrent();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const runOptimize = async () => {
    setOptimizing(true);
    try {
      const { data: started } = await api.post("/linkedin/optimize");
      const id = started.id;
      for (let i = 0; i < 80; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const opt = await api.get("/linkedin/optimizations");
        const found = opt.data.find((o) => o.id === id) || opt.data[0];
        if (found?.status === "done") { setOptimizations(opt.data); toast.success("Optimized content ready"); break; }
        if (found?.status === "error") { toast.error("Optimization failed. Please try again."); setOptimizations(opt.data); break; }
      }
      await loadCurrent();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setOptimizing(false); }
  };

  const setField = (k, v) => setSections((s) => ({ ...s, [k]: v }));
  const setExp = (i, k, v) => setSections((s) => { const e = [...s.experience]; e[i] = { ...e[i], [k]: v }; return { ...s, experience: e }; });
  const setEdu = (i, k, v) => setSections((s) => { const e = [...s.education]; e[i] = { ...e[i], [k]: v }; return { ...s, education: e }; });

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  if (!isPro) return (
    <div className="py-10" data-testid="linkedin-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight flex items-center justify-center gap-2"><Linkedin className="h-7 w-7 text-[#0a66c2]" /> LinkedIn Optimization</h1>
      </div>
      <ProFeatureGate
        description="Turn your LinkedIn profile into a recruiter magnet with AI — analyse it, then rewrite every section for your target role."
        features={["Recruiter-grade profile score", "AI-rewritten headline, About & experience", "Adjacent-role keyword targeting", "Photo & profile enhancement tips"]}
      />
    </div>
  );

  const latestOpt = optimizations.find((o) => o.status === "done" || o.content) || null;
  const analysis = current?.analysis;

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="linkedin-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-2"><Linkedin className="h-7 w-7 text-[#0a66c2]" /> LinkedIn Optimization</h1>
        <p className="text-muted-foreground mt-1 text-sm">Upload your LinkedIn PDF, review it, then let AI optimise it for your target role.</p>
      </div>

      {/* Step 1: Guide + Upload */}
      <Card className="p-6 border-zinc-200" data-testid="linkedin-upload-card">
        <h2 className="font-heading text-lg font-bold mb-2">1. Upload your LinkedIn profile PDF</h2>
        <div className="text-sm text-muted-foreground space-y-1.5 mb-4">
          <p><strong className="text-foreground">Desktop:</strong> Go to your LinkedIn profile → click <em>"More"</em> (sometimes <em>"Resources"</em>) near your headline → <em>"Save to PDF."</em></p>
          <p><strong className="text-foreground">Mobile:</strong> Open the LinkedIn app or a mobile browser → your profile → tap the three-dot <em>"More"</em> menu → <em>"Save to PDF."</em></p>
          <p className="flex items-start gap-1.5 text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800"><Info className="h-4 w-4 shrink-0 mt-0.5" /> LinkedIn's PDF export can drop or misformat some content (columns, icons). That's expected — you'll fix anything wrong in the next step.</p>
        </div>
        <label className="inline-flex">
          <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={onUpload} data-testid="linkedin-upload-input" />
          <Button asChild disabled={uploading} className="active:scale-[0.98] transition-transform"><span>{uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}{current?.has_profile ? "Upload a new PDF" : "Upload PDF"}</span></Button>
        </label>
      </Card>

      {/* Step 2: Review & edit */}
      {sections && (
        <Card className="p-6 border-zinc-200" data-testid="linkedin-review-card">
          <h2 className="font-heading text-lg font-bold mb-3">2. Review & edit what we extracted</h2>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-wider font-semibold">Headline</Label><Input value={sections.headline} onChange={(e) => setField("headline", e.target.value)} data-testid="li-headline" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-semibold">About</Label><Textarea rows={4} value={sections.about} onChange={(e) => setField("about", e.target.value)} data-testid="li-about" /></div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Experience</Label>
              <div className="space-y-3 mt-1">
                {(sections.experience || []).map((x, i) => (
                  <div key={i} className="rounded-lg border border-zinc-200 p-3 space-y-2" data-testid={`li-exp-${i}`}>
                    <div className="grid grid-cols-3 gap-2">
                      <Input placeholder="Title" value={x.title || ""} onChange={(e) => setExp(i, "title", e.target.value)} />
                      <Input placeholder="Company" value={x.company || ""} onChange={(e) => setExp(i, "company", e.target.value)} />
                      <Input placeholder="Dates" value={x.dates || ""} onChange={(e) => setExp(i, "dates", e.target.value)} />
                    </div>
                    <Textarea rows={2} placeholder="Description" value={x.description || ""} onChange={(e) => setExp(i, "description", e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Education</Label>
              <div className="space-y-2 mt-1">
                {(sections.education || []).map((x, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2" data-testid={`li-edu-${i}`}>
                    <Input placeholder="Degree" value={x.degree || ""} onChange={(e) => setEdu(i, "degree", e.target.value)} />
                    <Input placeholder="School" value={x.school || ""} onChange={(e) => setEdu(i, "school", e.target.value)} />
                    <Input placeholder="Dates" value={x.dates || ""} onChange={(e) => setEdu(i, "dates", e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider font-semibold">Skills (comma separated)</Label><Input value={(sections.skills || []).join(", ")} onChange={(e) => setField("skills", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="li-skills" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-semibold">Certifications (comma separated)</Label><Input value={(sections.certifications || []).join(", ")} onChange={(e) => setField("certifications", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="li-certs" /></div>
          </div>
          <Button onClick={saveReview} disabled={savingReview} className="mt-4 active:scale-[0.98] transition-transform" data-testid="li-save-review">
            {savingReview ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Save reviewed profile
          </Button>
        </Card>
      )}

      {/* Step 3: Target role */}
      {current?.reviewed && (
        <Card className="p-6 border-zinc-200" data-testid="linkedin-target-card">
          <h2 className="font-heading text-lg font-bold mb-3">3. Target role & seniority</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider font-semibold">Target role</Label><Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Procurement Manager" data-testid="li-target-role" /></div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Seniority</Label>
              <Select value={seniority} onValueChange={setSeniority}>
                <SelectTrigger data-testid="li-seniority"><SelectValue /></SelectTrigger>
                <SelectContent>{SENIORITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={setTarget} disabled={settingTarget} variant="outline" className="mt-3 active:scale-[0.98] transition-transform" data-testid="li-set-target">
            {settingTarget ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Set target & find adjacent roles
          </Button>
          {current?.adjacent_roles?.length > 0 && (
            <div className="mt-3 text-sm" data-testid="li-adjacent-roles">
              <span className="text-muted-foreground">Your profile will also be optimised to match: </span>
              {current.adjacent_roles.map((r) => <span key={r} className="inline-block bg-accent/10 text-accent text-xs font-medium rounded-full px-2.5 py-1 mr-1.5 mt-1">{r}</span>)}
            </div>
          )}
        </Card>
      )}

      {/* Step 4: Analyze */}
      {current?.target_role && (
        <Card className="p-6 border-zinc-200" data-testid="linkedin-analyze-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-bold">4. Analyse your profile</h2>
            <Button onClick={runAnalyze} disabled={analyzing || current.analyze_used_today} data-testid="li-analyze-btn" className="active:scale-[0.98] transition-transform">
              {analyzing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Gauge className="h-4 w-4 mr-1.5" />}{current.analyze_used_today && !analysis ? "Used today" : "Analyse (1/day)"}
            </Button>
          </div>
          {analysis && (
            <div className="space-y-4" data-testid="li-analysis-result">
              <div className="text-center">
                <div className="font-heading text-5xl font-bold">{analysis.overall_score}<span className="text-xl text-muted-foreground">/100</span></div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Overall LinkedIn score for {current.target_role}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">{SUBS.map(([k, l]) => <ScoreBar key={k} label={l} value={analysis.sub_scores?.[k] ?? 0} />)}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3"><p className="font-semibold text-green-800 text-sm mb-1">Strengths</p><ul className="text-sm text-green-900 space-y-1">{(analysis.strengths || []).map((s, i) => <li key={i}>• {s}</li>)}</ul></div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3"><p className="font-semibold text-red-800 text-sm mb-1">Weaknesses</p><ul className="text-sm text-red-900 space-y-1">{(analysis.weaknesses || []).map((s, i) => <li key={i}>• {s}</li>)}</ul></div>
              </div>
              <div><p className="font-semibold text-sm mb-1">Top fixes</p><ol className="text-sm space-y-1.5">{(analysis.top_fixes || []).map((f, i) => <li key={i}><strong>{i + 1}. {f.fix}</strong> <span className="text-muted-foreground">— {f.why}</span></li>)}</ol></div>
            </div>
          )}
        </Card>
      )}

      {/* Progress tracker */}
      {history.length > 0 && <ProgressCard history={history} />}

      {/* Step 5: Optimize */}
      {current?.has_valid_analysis && (
        <Card className="p-6 border-zinc-200" data-testid="linkedin-optimize-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-bold">5. Optimise your profile</h2>
            <Button onClick={runOptimize} disabled={optimizing || current.optimize_used_today} data-testid="li-optimize-btn" className="active:scale-[0.98] transition-transform">
              {optimizing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{current.optimize_used_today && !latestOpt ? "Used today" : "Optimise (1/day)"}
            </Button>
          </div>
          {latestOpt && <OptimizationView opt={latestOpt} />}
        </Card>
      )}
    </div>
  );
}

function Block({ title, instruction, children, text }) {
  return (
    <div className="space-y-1.5" data-testid={`opt-block-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="font-semibold text-sm">{title}</p>
        {text != null && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => copy(text)}><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>}
      </div>
      {instruction && <p className="text-xs bg-accent/10 text-accent-foreground border border-accent/20 rounded px-2 py-1 font-medium text-[#14532d]">{instruction}</p>}
      {children}
    </div>
  );
}

function OptimizationView({ opt }) {
  const c = opt.content || {};
  return (
    <div className="space-y-4 mt-2" data-testid="li-optimization-result">
      <div className="flex justify-end">
        <a href={`${API}/linkedin/optimizations/${opt.id}/download`} target="_blank" rel="noreferrer" data-testid="li-download-opt">
          <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1.5" /> Download PDF</Button>
        </a>
      </div>
      <Block title="Headline" instruction={c.headline?.instruction} text={c.headline?.content}><p className="text-sm bg-zinc-50 border rounded p-2">{c.headline?.content}</p></Block>
      <Block title="About" instruction={c.about?.instruction} text={c.about?.content}><p className="text-sm bg-zinc-50 border rounded p-2 whitespace-pre-wrap">{c.about?.content}</p></Block>
      <div>
        <p className="font-semibold text-sm mb-2">Experience</p>
        <div className="space-y-3">
          {(c.experience || []).map((x, i) => (
            <Block key={i} title={`${x.title} — ${x.company}`} instruction={x.instruction} text={x.content}>
              <p className="text-sm bg-zinc-50 border rounded p-2 whitespace-pre-wrap">{x.content}</p>
            </Block>
          ))}
        </div>
      </div>
      <Block title="Skills" instruction={c.skills?.instruction} text={(c.skills?.list || []).join(", ")}>
        <div className="flex flex-wrap gap-1.5">{(c.skills?.list || []).map((s) => <span key={s} className="bg-zinc-100 rounded-full px-2.5 py-1 text-xs">{s}</span>)}</div>
      </Block>
      <div><p className="font-semibold text-sm mb-1">Profile photo guidance</p><ul className="text-sm text-muted-foreground space-y-1">{(c.photo_guidance || []).map((t, i) => <li key={i}>• {t}</li>)}</ul></div>
      <div><p className="font-semibold text-sm mb-1">Further enhancements</p><ul className="text-sm text-muted-foreground space-y-1">{(c.enhancement_tips || []).map((t, i) => <li key={i}>• {t}</li>)}</ul></div>
    </div>
  );
}
